'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';

import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  AtSign,
  Check,
  ChevronDown,
  type LucideIcon,
  Mail,
  Maximize2,
  MessageCircle,
  MessageSquare,
  Phone,
  Slack,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { SubtleEntrance } from '@/components/ui/subtle-entrance';
import { isRTL } from '@/lib/i18n';
import { useLocale } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

/** A single block in the premium (sub-landing-page) modal layout. */
export type ModalBlock =
  | { type: 'text'; heading?: string; body: string | string[] }
  | { type: 'video'; src: string; poster?: string; caption?: string }
  | { type: 'list'; heading?: string; items: string[] }
  | { type: 'stats'; items: { value: string; label: string }[] }
  | {
      type: 'channels';
      heading?: string;
      subheading?: string;
      items: { name: string; href: string }[];
    }
  | {
      type: 'logos';
      /** 'cards' = roomy logo+name rows; 'grid' = dense icon tiles. */
      variant?: 'cards' | 'grid';
      heading?: string;
      subheading?: string;
      items: { name: string; src?: string; emoji?: string; href: string }[];
    }
  | { type: 'faq'; heading?: string; items: { q: string; a: string }[] };

export interface BentoCard {
  id: string;
  /** Optional single emoji rendered above the title. */
  emoji?: string;
  /** Optional thin-stroke line icon rendered above the title.
   *  Takes precedence over `emoji` when both are set. */
  icon?: LucideIcon;
  title: string;
  description: string;
  /** Optional. When omitted, the card renders as text-only (no visual). */
  image?: string;
  layout?: 'vertical' | 'horizontal';
  className?: string;
  modal: {
    eyebrow: string;
    headline: string;
    /** Premium modal: a one-line intro under the headline. */
    lead?: string;
    /** Premium modal: rich, scrollable content (text / video / list / stats).
     *  When present, the modal renders the sub-landing-page layout. */
    blocks?: ModalBlock[];
    /** Premium modal: closing call-to-action. */
    cta?: { label: string; href: string };
    /** Legacy modal (used by other bento sections). */
    paragraphs?: string[];
    bullets?: string[];
  };
}

export interface BentoGridProps {
  /** Uppercase label above the title. Omit to hide the eyebrow entirely. */
  eyebrow?: string;
  /** Section headline */
  title: ReactNode;
  /** Optional subhead paragraph */
  subtitle?: ReactNode;
  /** Cards to render — wide cards (layout: 'horizontal') will span all 3 columns */
  cards: BentoCard[];
  /** Background color of the section (defaults to sand) */
  background?: string;
  /** Show the expand affordance on every card without waiting for hover. */
  alwaysShowExpand?: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Bleed image visuals                                                       */
/* -------------------------------------------------------------------------- */

const BleedImage = ({ src }: { src: string }) => (
  <div className="pointer-events-none absolute -bottom-6 w-[92%] sm:w-[88%] ltr:-right-4 rtl:-left-4">
    <div className="radius-18 aspect-[16/11] overflow-hidden bg-white shadow-[0_25px_60px_-25px_rgba(0,0,0,0.3),0_2px_8px_-2px_rgba(0,0,0,0.08)] ring-1 ring-neutral-200/80">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="block h-full w-full object-cover ltr:object-left-top rtl:object-right-top"
      />
    </div>
  </div>
);

const WideBleed = ({ src }: { src: string }) => (
  <>
    {/* Mobile: bottom bleed on the inline-end side (matches the small cards) */}
    <div className="pointer-events-none absolute -bottom-6 w-[92%] sm:w-[88%] md:hidden ltr:-right-4 rtl:-left-4">
      <div className="radius-18 aspect-[16/11] overflow-hidden bg-white shadow-[0_25px_60px_-25px_rgba(0,0,0,0.3),0_2px_8px_-2px_rgba(0,0,0,0.08)] ring-1 ring-neutral-200/80">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="block h-full w-full object-cover ltr:object-left-top rtl:object-right-top"
        />
      </div>
    </div>

    {/* Desktop: fills the far half, bleeds past the inline-end + bottom edges.
        Mirrors to the left in RTL so it never overlaps the text column. */}
    <div className="pointer-events-none absolute -bottom-6 top-4 hidden w-[50%] md:block ltr:-right-6 rtl:-left-6">
      <div className="radius-18 h-full overflow-hidden bg-white shadow-[0_25px_60px_-25px_rgba(0,0,0,0.3),0_2px_8px_-2px_rgba(0,0,0,0.08)] ring-1 ring-neutral-200/80">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="block h-full w-full object-cover object-top"
        />
      </div>
    </div>
  </>
);

/* -------------------------------------------------------------------------- */
/*  Card                                                                      */
/* -------------------------------------------------------------------------- */

interface CardProps {
  card: BentoCard;
  onOpen: () => void;
  /** Show the expand affordance without waiting for hover. */
  alwaysShowExpand?: boolean;
}

const Card = ({ card, onOpen, alwaysShowExpand }: CardProps) => {
  const locale = useLocale();
  const isHorizontal = card.layout === 'horizontal';
  const hasImage = Boolean(card.image);

  return (
    <button
      type="button"
      onClick={onOpen}
      // aria-label must start with the visible title (the rest is fine to
      // append) — Lighthouse flags mismatched accessible names otherwise.
      aria-label={`${card.title}${locale === 'ar' ? '، افتح التفاصيل' : ', open details'}`}
      className={cn(
        'radius-18 group relative block h-full w-full overflow-hidden border border-neutral-300/80 bg-white ltr:text-left rtl:text-right',
        'shadow-[0_1px_0_rgba(0,0,0,0.02)]',
        'transition-shadow duration-300 hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.12)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/40',
        isHorizontal && 'md:col-span-3',
        card.className,
      )}
    >
      {/* Unified gradient backdrop — present on all cards so the warm sand
       * tint always reads (with or without an image bleed). */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0',
          isHorizontal && hasImage
            ? 'from-white via-white to-[#f3f1ec] md:via-[55%] ltr:bg-gradient-to-r rtl:bg-gradient-to-l'
            : 'bg-gradient-to-b from-white via-white to-[#f3f1ec] via-[40%]',
        )}
      />
      {/* Subtle dot grid masked toward the bottom (or visual side) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)',
          backgroundSize: '18px 18px',
          maskImage:
            isHorizontal && hasImage
              ? 'linear-gradient(to right, transparent 45%, black 80%, black 100%)'
              : 'linear-gradient(to bottom, transparent 35%, black 70%, black 100%)',
          WebkitMaskImage:
            isHorizontal && hasImage
              ? 'linear-gradient(to right, transparent 45%, black 80%, black 100%)'
              : 'linear-gradient(to bottom, transparent 35%, black 70%, black 100%)',
        }}
      />

      {/* Expand affordance */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-5 z-20 flex h-8 w-8 items-center justify-center rounded-full ltr:right-5 rtl:left-5',
          'bg-neutral-900/85 text-white shadow-[0_4px_16px_-4px_rgba(0,0,0,0.3)] backdrop-blur',
          'transition-all duration-200 ease-out',
          // Opt-in per section. On the six-agent grid the cards are the only
          // thing on the page that opens a modal, so the affordance has to be
          // visible without pointing at them — and on touch there is no hover
          // to reveal it at all. Everywhere else it stays a hover reveal,
          // because a persistent badge collides with longer card headings.
          alwaysShowExpand
            ? 'scale-100 opacity-100 group-hover:scale-110'
            : 'scale-90 opacity-0 group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100',
        )}
      >
        <Maximize2 className="h-3.5 w-3.5" strokeWidth={2.25} />
      </span>

      <div
        className={cn(
          'relative flex h-full',
          isHorizontal && hasImage
            ? 'flex-col md:grid md:grid-cols-2 md:items-stretch'
            : 'flex-col',
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-3 p-8 sm:p-10',
            isHorizontal && hasImage && 'md:justify-center md:p-12',
          )}
        >
          {card.icon ? (
            <card.icon
              aria-hidden
              className="mb-1 h-7 w-7 text-neutral-900"
              strokeWidth={1.5}
            />
          ) : (
            card.emoji && (
              <span
                aria-hidden
                className="mb-1 block text-3xl leading-none md:text-4xl"
              >
                {card.emoji}
              </span>
            )
          )}
          {/* Card titles sit a step below the section heading — lighter
              weight (400) so the section h2 stays the clear primary heading. */}
          <h3 className="font-serif text-xl !font-normal text-neutral-900 md:text-2xl">
            {card.title}
          </h3>
          <p className="max-w-[38ch] text-sm leading-relaxed text-neutral-500 md:text-[15px]">
            {card.description}
          </p>
        </div>
        {hasImage && (
          <div
            className={cn(
              'flex items-center justify-center px-6 pt-8 pb-10 sm:px-8',
              isHorizontal ? 'min-h-[260px]' : 'mt-auto min-h-[220px]',
            )}
          >
            {/* The bleed images are absolutely positioned, so they contribute
                no height. Their real height is driven by the card width
                (`w-[92%]` at `aspect-[16/11]` ≈ 0.63 × card width), while the
                `min-h-*` above is a fixed pixel floor — so once the card gets
                wide enough the image grows past the reserved space and rides up
                over the copy. That is what happens below `md`, where the outer
                grid collapses from three columns to one and every card becomes
                full-width.

                This spacer reserves the same aspect ratio in the flow, so the
                gap always grows with the image. It sits inside the padding, so
                it over-reserves slightly — harmless — rather than under. The
                `md:hidden` matches `WideBleed`, whose desktop variant is
                height-driven by the card and needs no reservation. */}
            <div
              aria-hidden
              className={cn(
                'w-full shrink-0 aspect-[16/11]',
                isHorizontal && 'md:hidden',
              )}
            />
            {isHorizontal ? (
              <WideBleed src={card.image!} />
            ) : (
              <BleedImage src={card.image!} />
            )}
          </div>
        )}
      </div>
    </button>
  );
};

/* -------------------------------------------------------------------------- */
/*  Modal                                                                     */
/* -------------------------------------------------------------------------- */

interface CardModalProps {
  card: BentoCard | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/* Scroll-triggered reveal — fades/rises each block as it enters the modal
 * viewport. Uses the viewport root (works inside the scroll container because
 * scrolling moves the blocks through the viewport). */
const Reveal = ({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={cn(
        'transition-[opacity,transform] duration-[650ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform',
        shown ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
        className,
      )}
    >
      {children}
    </div>
  );
};

/* A muted, looping video that only plays while on-screen (perf + smoothness). */
const ModalVideo = ({
  src,
  poster,
  caption,
}: {
  src: string;
  poster?: string;
  caption?: string;
}) => {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const p = v.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <figure className="my-2">
      <video
        ref={ref}
        // #t=0.1 paints the first frame as a resting poster (no black box)
        // when a poster isn't supplied and before the clip scrolls into view.
        src={poster || src.includes('#') ? src : `${src}#t=0.1`}
        poster={poster}
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden
        className="aspect-video w-full bg-neutral-100 object-cover ring-1 ring-neutral-900/5"
      />
      {caption && (
        <figcaption className="mt-3 text-center text-[13px] text-neutral-400">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

/* Channel → icon map for the 'channels' block. Falls back to a chat bubble. */
const CHANNEL_ICONS: Record<string, LucideIcon> = {
  Voice: Phone,
  Chat: MessageCircle,
  Email: Mail,
  WhatsApp: MessageSquare,
  Slack: Slack,
  Social: AtSign,
};

/* A clean, smoothly-animating FAQ accordion. */
const FaqBlock = ({
  heading,
  items,
}: {
  heading?: string;
  items: { q: string; a: string }[];
}) => {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div>
      {heading && (
        <h3 className="mb-6 font-serif text-xl text-neutral-900 md:text-2xl">
          {heading}
        </h3>
      )}
      <div className="border-t border-neutral-200">
        {items.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="border-b border-neutral-200">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-6 py-5 text-left"
              >
                <span className="text-[15px] font-medium text-neutral-900 md:text-base">
                  {item.q}
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-300',
                    isOpen && 'rotate-180 text-neutral-900',
                  )}
                />
              </button>
              <div
                className={cn(
                  'grid transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
                  isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="overflow-hidden">
                  <p className="max-w-[58ch] pb-5 text-[15px] leading-relaxed text-neutral-500">
                    {item.a}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* Renders one premium content block. */
const ModalContentBlock = ({ block }: { block: ModalBlock }) => {
  if (block.type === 'faq') {
    return <FaqBlock heading={block.heading} items={block.items} />;
  }
  if (block.type === 'channels') {
    return (
      <div>
        {block.heading && (
          <h3 className="font-serif text-xl text-neutral-900 md:text-2xl">
            {block.heading}
          </h3>
        )}
        {block.subheading && (
          <p className="mt-2 text-[15px] leading-relaxed text-neutral-500">
            {block.subheading}
          </p>
        )}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {block.items.map((ch) => {
            const Icon = CHANNEL_ICONS[ch.name] ?? MessageCircle;
            return (
              <Link
                key={ch.name}
                href={ch.href}
                prefetch
                className="group flex items-center gap-3 border border-neutral-200 bg-white p-4 transition-colors hover:border-neutral-900/20 hover:bg-neutral-50"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-neutral-900 text-white">
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </span>
                <span className="text-sm font-medium text-neutral-900">
                  {ch.name}
                </span>
                <ArrowUpRight className="ms-auto h-4 w-4 shrink-0 text-neutral-300 transition-colors group-hover:text-neutral-900" />
              </Link>
            );
          })}
        </div>
      </div>
    );
  }
  if (block.type === 'logos') {
    const dense = block.variant === 'grid';
    return (
      <div>
        {block.heading && (
          <h3 className="font-serif text-xl text-neutral-900 md:text-2xl">
            {block.heading}
          </h3>
        )}
        {block.subheading && (
          <p className="mt-2 text-[15px] leading-relaxed text-neutral-500">
            {block.subheading}
          </p>
        )}
        <div
          className={cn(
            'mt-6 grid gap-2',
            dense
              ? 'grid-cols-3 sm:grid-cols-5'
              : 'grid-cols-2 gap-3 sm:grid-cols-3',
          )}
        >
          {block.items.map((it) => {
            const icon = it.src ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={it.src}
                alt={it.name}
                loading="lazy"
                decoding="async"
                className="h-6 w-6 shrink-0 object-contain"
              />
            ) : (
              <span className="text-lg leading-none" aria-hidden>
                {it.emoji ?? '🔗'}
              </span>
            );
            return (
              <Link
                key={it.name}
                href={it.href}
                prefetch={!dense}
                title={it.name}
                className={cn(
                  'group border border-neutral-200 bg-white transition-colors hover:border-neutral-900/20 hover:bg-neutral-50',
                  dense
                    ? 'flex flex-col items-center justify-center gap-2 px-2 py-4 text-center'
                    : 'flex items-center gap-3 p-4',
                )}
              >
                {icon}
                <span
                  className={cn(
                    dense
                      ? 'line-clamp-1 text-[11px] leading-tight text-neutral-500'
                      : 'text-sm font-medium text-neutral-900',
                  )}
                >
                  {it.name}
                </span>
                {!dense && (
                  <ArrowUpRight className="ms-auto h-4 w-4 shrink-0 text-neutral-300 transition-colors group-hover:text-neutral-900" />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    );
  }
  if (block.type === 'video') {
    return (
      <ModalVideo
        src={block.src}
        poster={block.poster}
        caption={block.caption}
      />
    );
  }
  if (block.type === 'stats') {
    return (
      <dl className="grid grid-cols-3 gap-4 border-y border-neutral-200 py-8">
        {block.items.map((s, i) => (
          <div key={i} className="text-center">
            <dt className="font-serif text-3xl text-neutral-900 md:text-4xl">
              {s.value}
            </dt>
            <dd className="mt-1 text-xs tracking-wide text-neutral-500 uppercase">
              {s.label}
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  if (block.type === 'list') {
    return (
      <div>
        {block.heading && (
          <h3 className="mb-5 font-serif text-xl text-neutral-900 md:text-2xl">
            {block.heading}
          </h3>
        )}
        <ul className="grid gap-x-8 gap-y-3.5 sm:grid-cols-2">
          {block.items.map((it, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-[15px] leading-relaxed text-neutral-700"
            >
              <Check
                className="mt-0.5 h-4 w-4 shrink-0 text-neutral-900"
                strokeWidth={2.5}
              />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  // text
  const paras = Array.isArray(block.body) ? block.body : [block.body];
  return (
    <div className="max-w-[62ch]">
      {block.heading && (
        <h3 className="mb-4 font-serif text-xl text-neutral-900 md:text-2xl">
          {block.heading}
        </h3>
      )}
      <div className="space-y-4 text-[15px] leading-[1.75] text-neutral-600 md:text-base">
        {paras.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </div>
  );
};

const CardModal = ({ card, open, onOpenChange }: CardModalProps) => {
  if (!card) return null;

  const isPremium = Boolean(card.modal.blocks?.length);

  if (isPremium) {
    const blocks = card.modal.blocks!;
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            // NB: no `relative` here — Radix DialogContent is already
            // `fixed`, which is the positioning context for the floating CTA.
            // Adding `relative` overrides `fixed` and breaks modal centering.
            'overflow-hidden border-0 bg-white p-0',
            'w-[calc(100vw-1.5rem)] max-w-[900px]',
            'duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
            'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
            'data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-bottom-4',
          )}
        >
          <div className="max-h-[92vh] overflow-y-auto scroll-smooth overscroll-contain [scrollbar-width:thin]">
            {/* Hero */}
            <header className="px-6 pt-14 pb-2 sm:px-14 sm:pt-20">
              <p className="text-[11px] font-medium tracking-[0.18em] text-neutral-400 uppercase">
                {card.modal.eyebrow}
              </p>
              <DialogTitle className="mt-5 max-w-[20ch] font-serif text-3xl leading-[1.1] text-balance text-neutral-900 md:text-[2.5rem]">
                {card.modal.headline}
              </DialogTitle>
              {card.modal.lead && (
                <DialogDescription className="mt-5 max-w-[54ch] text-lg leading-relaxed text-neutral-500">
                  {card.modal.lead}
                </DialogDescription>
              )}
            </header>

            {/* Body blocks — generous rhythm, scroll-revealed. Extra bottom
                padding leaves room for the floating CTA. */}
            <div
              className={cn(
                'flex flex-col gap-14 px-6 pt-14 sm:gap-20 sm:px-14 sm:pt-20',
                card.modal.cta ? 'pb-28 sm:pb-32' : 'pb-14 sm:pb-20',
              )}
            >
              {blocks.map((block, i) => (
                <Reveal key={i}>
                  <ModalContentBlock block={block} />
                </Reveal>
              ))}
            </div>
          </div>

          {/* Floating CTA — pinned to the bottom of the modal, always in reach.
              A soft fade lets content scroll away cleanly beneath it. */}
          {card.modal.cta && (
            <>
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-28 bg-gradient-to-t from-white via-white/85 to-transparent"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-end px-6 pb-6 sm:px-14 sm:pb-8">
                <Button
                  asChild
                  className="pointer-events-auto h-12 bg-neutral-900 px-7 text-sm font-medium text-white shadow-[0_12px_32px_-8px_rgba(0,0,0,0.4)] hover:bg-neutral-800"
                >
                  <Link href={card.modal.cta.href} prefetch>
                    {card.modal.cta.label}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // Legacy layout (other bento sections)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'overflow-hidden border-0 p-0',
          'w-[calc(100vw-2rem)] max-w-[680px] sm:w-[calc(100vw-3rem)]',
          'duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
          'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          'data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-bottom-4',
        )}
      >
        <div className="flex max-h-[88vh] flex-col overflow-y-auto">
          {card.image && (
            <div className="relative h-56 w-full shrink-0 bg-[#f3f1ec] sm:h-72 md:h-80">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={card.image}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover object-left-top"
              />
            </div>
          )}

          <div className="flex flex-col gap-5 p-7 sm:p-10">
            <p className="text-[11px] font-medium tracking-widest text-neutral-500 uppercase">
              {card.modal.eyebrow}
            </p>
            <DialogTitle className="font-serif font-normal text-balance text-neutral-900 text-2xl leading-tight md:text-3xl">
              {card.modal.headline}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-4 text-sm leading-relaxed text-neutral-600 md:text-[15px]">
                {card.modal.paragraphs?.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </DialogDescription>

            {card.modal.bullets && (
              <ul className="mt-2 space-y-2.5 border-t border-neutral-200 pt-5">
                {card.modal.bullets.map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-sm text-neutral-700 md:text-[15px]"
                  >
                    <span
                      aria-hidden
                      className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400"
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------------------------------------------------------------- */
/*  Cards-only (grid + modal, no section/header) – reusable inline            */
/* -------------------------------------------------------------------------- */

export interface BentoCardsProps {
  cards: BentoCard[];
  className?: string;
  /** Show the expand affordance without waiting for hover. Opt-in: a
   *  persistent badge overlaps longer card headings. */
  alwaysShowExpand?: boolean;
}

export const BentoCards = ({
  cards,
  className,
  alwaysShowExpand,
}: BentoCardsProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeCard = cards.find((c) => c.id === activeId) ?? null;

  return (
    <>
      <div
        className={cn(
          'grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5',
          className,
        )}
      >
        {cards.map((card) => (
          <Card
            key={card.id}
            card={card}
            onOpen={() => setActiveId(card.id)}
            alwaysShowExpand={alwaysShowExpand}
          />
        ))}
      </div>

      <CardModal
        card={activeCard}
        open={activeId !== null}
        onOpenChange={(o) => {
          if (!o) setActiveId(null);
        }}
      />
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*  Slider variant (horizontal scroll + arrow controls)                       */
/* -------------------------------------------------------------------------- */

/** Distance from the viewport edge to the centred container's content edge.
 *  `cqw`, resolved against the carousel viewport, which excludes the scrollbar
 *  — so this lands exactly on the centred container's edge. `100vw` includes
 *  the scrollbar and drifts ~7px right of everything else, and `100%` is worse
 *  still: the track resolves it against the viewport but a flex item inside
 *  resolves it against the track's content box, so one expression silently
 *  produces two different widths. */
const SLIDER_EDGE_INSET =
  'max(var(--header-padding-x), calc((100cqw - 1152px) / 2 + var(--header-padding-x)))';

export interface BentoCardsSliderProps {
  cards: BentoCard[];
  /** Visual theme of the slider arrows (defaults to light for sand/white bgs). */
  theme?: 'light' | 'dark';
  className?: string;
  /** Extra classes applied to the arrow controls row. Useful when the slider
   * bleeds past the container and you want to pull the arrows back to the
   * container's right edge (e.g. `pr-[calc((100vw-1152px)/2+1.5rem)]`). */
  controlsClassName?: string;
}

export const BentoCardsSlider = ({
  cards,
  theme = 'light',
  className,
  controlsClassName,
}: BentoCardsSliderProps) => {
  const locale = useLocale();
  const rtl = isRTL(locale);
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeCard = cards.find((c) => c.id === activeId) ?? null;

  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };
    update();
    api.on('select', update);
    api.on('reInit', update);
    return () => {
      api.off('select', update);
      api.off('reInit', update);
    };
  }, [api]);

  const arrowBase =
    'flex h-10 w-10 items-center justify-center rounded-full border transition-all disabled:opacity-30 disabled:cursor-not-allowed';
  const arrowTheme =
    theme === 'dark'
      ? 'border-white/15 bg-white/[0.06] text-white hover:bg-white/15'
      : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50';

  return (
    <div className={cn('relative', className)}>
      <Carousel
        setApi={setApi}
        opts={{
          align: 'start',
          containScroll: 'trimSnaps',
          direction: rtl ? 'rtl' : 'ltr',
        }}
        className="w-full"
      >
        <CarouselContent
          className={cn('ltr:-ml-4 rtl:-mr-4 md:ltr:-ml-5 md:rtl:-mr-5')}
          // Push the entire flex track toward the reading-start edge so the
          // first card aligns with the container's edge, while the embla
          // viewport still stretches to the browser's edge — so cards bleed
          // off both sides on scroll instead of being clipped at the
          // container edge. Mirrored for RTL since embla lays cards out in
          // DOM order regardless of `dir`.
          //
          // `100%` (not `100vw`) on purpose: the track's containing block is
          // the full-bleed carousel viewport, which excludes the scrollbar, so
          // this lands exactly on the centred container's edge. `100vw`
          // includes the scrollbar and drifts ~7px right of everything else.
          //
          // Start side only. The end inset cannot live here: embla derives its
          // scroll limit from the slides it measures, and trailing padding on
          // the flex container is not a slide, so it adds nothing to the
          // scrollable extent. The spacer below is what stops the last card.
          style={{ paddingInlineStart: SLIDER_EDGE_INSET }}
        >
          {cards.map((card) => (
            <CarouselItem
              key={card.id}
              // Fixed card widths so each slide is a consistent size regardless
              // of how wide the slider's parent is (especially when bleeding).
              className="basis-[280px] ltr:pl-4 rtl:pr-4 sm:basis-[320px] md:basis-[340px] md:ltr:pl-5 md:rtl:pr-5 lg:basis-[360px]"
            >
              <Card card={card} onOpen={() => setActiveId(card.id)} />
            </CarouselItem>
          ))}
          {/* Trailing spacer. embla measures the container's children, so this
           *  counts toward the scroll limit where padding does not — which is
           *  what lets the last card travel far enough to finish level with
           *  the container's edge, beside the forward arrow. trimSnaps drops
           *  the snap it would otherwise add, so nothing scrolls to it. */}
          <div
            aria-hidden="true"
            className="shrink-0"
            style={{ flexBasis: SLIDER_EDGE_INSET }}
          />
        </CarouselContent>

        {/* Controls sit just below the slider, right-aligned */}
        <div
          className={cn(
            'mt-6 flex items-center justify-end gap-2 md:mt-8',
            controlsClassName,
          )}
        >
          <button
            type="button"
            aria-label={locale === 'ar' ? 'السابق' : 'Previous'}
            onClick={() => api?.scrollPrev()}
            disabled={!canScrollPrev}
            className={cn(arrowBase, arrowTheme)}
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          </button>
          <button
            type="button"
            aria-label={locale === 'ar' ? 'التالي' : 'Next'}
            onClick={() => api?.scrollNext()}
            disabled={!canScrollNext}
            className={cn(arrowBase, arrowTheme)}
          >
            <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </button>
        </div>
      </Carousel>

      <CardModal
        card={activeCard}
        open={activeId !== null}
        onOpenChange={(o) => {
          if (!o) setActiveId(null);
        }}
      />
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Section                                                                   */
/* -------------------------------------------------------------------------- */

export const BentoGrid = ({
  eyebrow,
  title,
  subtitle,
  cards,
  background = '#fafaf9',
  alwaysShowExpand,
}: BentoGridProps) => (
  <section className="py-16 md:py-32" style={{ backgroundColor: background }}>
    <div className="container">
      <SubtleEntrance>
        <div className="mb-14 max-w-2xl md:mb-20">
          {eyebrow && (
            <p className="mb-4 text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {eyebrow}
            </p>
          )}
          <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {subtitle}
            </p>
          )}
        </div>
      </SubtleEntrance>

      <SubtleEntrance delay={0.1}>
        <BentoCards cards={cards} alwaysShowExpand={alwaysShowExpand} />
      </SubtleEntrance>
    </div>
  </section>
);

export default BentoGrid;
